import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  CREATED_RANGE_PRESETS,
  type OpportunityCreatedRange,
} from "./opportunityFilterTypes";

interface OpportunityCreatedFilterContentProps {
  value: OpportunityCreatedRange | null;
  onChange: (range: OpportunityCreatedRange | null) => void;
}

/**
 * Dropdown body for the Created filter: the rolling-window presets plus a
 * calendar for an explicit range.
 * @param value - The current created-date selection, or null when off
 * @param onChange - Called with the next selection, or null to clear it
 * @returns The created-filter dropdown content
 */
export function OpportunityCreatedFilterContent({
  value,
  onChange,
}: OpportunityCreatedFilterContentProps) {
  const selectedRange =
    value?.kind === "custom" ? { from: value.from, to: value.to } : undefined;

  return (
    <div>
      <Command>
        <CommandList>
          <CommandGroup>
            {CREATED_RANGE_PRESETS.map((preset) => (
              <CommandItem
                key={preset.value}
                value={preset.value}
                onSelect={() =>
                  onChange({ kind: "preset", preset: preset.value })
                }
                className="justify-between"
              >
                {preset.label}
                {value?.kind === "preset" && value.preset === preset.value && (
                  <Check className="h-4 w-4" />
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>

      <div className="border-t">
        <Calendar
          mode="range"
          selected={selectedRange}
          defaultMonth={selectedRange?.from}
          numberOfMonths={1}
          onSelect={(range) =>
            onChange(
              range?.from
                ? { kind: "custom", from: range.from, to: range.to }
                : null,
            )
          }
        />
      </div>

      {value && (
        <div className="flex justify-end border-t px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground"
            onClick={() => onChange(null)}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
}
